/**
 * vmx-review-counts.js
 *
 * Global review-stats updater. Fetches /assets/vmx-reviews-data.json once,
 * computes count + average score per product slug, then updates every
 * matching element on the page.
 *
 * Markup contract:
 *   <span data-vmx-review-count data-product-slug="snooze-dose">…</span>
 *   <span data-vmx-review-score data-product-slug="snooze-dose">…</span>
 *   <span data-vmx-review-stars data-product-slug="snooze-dose">…</span>
 *
 * Optional formatting:
 *   data-format="count-only"    → "62"
 *   data-format="count-suffix"  → "62 reviews"  (default for count)
 *   data-format="count-paren"   → "(62)"
 *   data-format="score-one-dp"  → "4.8"   (default for score)
 *   data-format="score-of-five" → "4.8 / 5"
 *
 * Caches result on window.__vmxReviewStats so multiple components share one fetch.
 */
(function () {
  'use strict';

  var STATS_PROMISE_KEY = '__vmxReviewStatsPromise';
  var STATS_CACHE_KEY   = '__vmxReviewStats';

  function fetchStats() {
    if (window[STATS_PROMISE_KEY]) return window[STATS_PROMISE_KEY];

    // Discover the asset URL — prefer window.VMX_REVIEWS_SRC (set globally in
    // theme.liquid head), fall back to a DOM probe (PDP reviews section
    // carries it as a data-attribute on Shopify's CDN-fingerprinted URL).
    var src = window.VMX_REVIEWS_SRC;
    if (!src) {
      var probe = document.querySelector('[data-reviews-src]');
      if (probe) src = probe.getAttribute('data-reviews-src');
    }
    if (!src) return Promise.resolve(null);

    window[STATS_PROMISE_KEY] = fetch(src, { credentials: 'omit' })
      .then(function (r) { return r.json(); })
      .then(function (data) {
        var stats = {};
        (data.reviews || []).forEach(function (review) {
          var slug = review.productSlug;
          if (!slug) return;
          if (!stats[slug]) stats[slug] = { count: 0, sumStars: 0 };
          stats[slug].count += 1;
          stats[slug].sumStars += Number(review.stars) || 0;
        });
        // Aggregate "all" record covers global trust strips (nav drawer, footer, etc.)
        var allCount = 0, allSum = 0;
        Object.keys(stats).forEach(function (slug) {
          var s = stats[slug];
          s.avg = s.count > 0 ? s.sumStars / s.count : 0;
          allCount += s.count;
          allSum   += s.sumStars;
        });
        // Prefer Judge.me's live shop-wide review total (server-rendered into
        // window.VMX_JM_SHOP_REVIEWS from shop.metafields.judgeme.shop_reviews_count)
        // so every [data-product-slug="all"] count tracks the real Judge.me number.
        // Falls back to the local aggregate when the metafield is empty/0.
        var jmAll = Number(window.VMX_JM_SHOP_REVIEWS) || 0;
        stats.all = {
          count: jmAll > 0 ? jmAll : allCount,
          sumStars: allSum,
          avg: allCount > 0 ? allSum / allCount : 0
        };
        window[STATS_CACHE_KEY] = stats;
        return stats;
      })
      .catch(function () { return null; });

    return window[STATS_PROMISE_KEY];
  }

  function formatCount(n, format) {
    switch (format) {
      case 'count-only':   return String(n);
      case 'count-paren':  return '(' + n + ')';
      case 'count-suffix':
      default:             return n + (n === 1 ? ' review' : ' reviews');
    }
  }

  function formatScore(avg, format) {
    var oneDp = (Math.round(avg * 10) / 10).toFixed(1);
    if (format === 'score-of-five') return oneDp + ' / 5';
    return oneDp;
  }

  function makeStars(avg) {
    var full = Math.round(avg);
    return '★★★★★'.slice(0, full) + '☆☆☆☆☆'.slice(0, 5 - full);
  }

  function paint(stats) {
    if (!stats) return;

    document.querySelectorAll('[data-vmx-review-count][data-product-slug]').forEach(function (el) {
      var slug = el.getAttribute('data-product-slug');
      var record = stats[slug];
      if (!record) {
        el.textContent = '0 reviews';
        el.setAttribute('data-vmx-review-empty', 'true');
        return;
      }
      var fmt = el.getAttribute('data-format') || 'count-suffix';
      el.textContent = formatCount(record.count, fmt);
      el.removeAttribute('data-vmx-review-empty');
    });

    document.querySelectorAll('[data-vmx-review-score][data-product-slug]').forEach(function (el) {
      var slug = el.getAttribute('data-product-slug');
      var record = stats[slug];
      if (!record || record.count === 0) return;
      var fmt = el.getAttribute('data-format') || 'score-one-dp';
      el.textContent = formatScore(record.avg, fmt);
    });

    document.querySelectorAll('[data-vmx-review-stars][data-product-slug]').forEach(function (el) {
      var slug = el.getAttribute('data-product-slug');
      var record = stats[slug];
      if (!record || record.count === 0) return;
      el.textContent = makeStars(record.avg);
      el.setAttribute('aria-label', formatScore(record.avg, 'score-one-dp') + ' out of 5 stars');
    });

    // Fire an event so other components (e.g. the reviews preview) can re-render
    // without re-fetching.
    document.dispatchEvent(new CustomEvent('vmx:review-stats:ready', { detail: { stats: stats } }));
  }

  function init() {
    var hasTargets = document.querySelector('[data-vmx-review-count],[data-vmx-review-score],[data-vmx-review-stars]');
    if (!hasTargets) return;
    fetchStats().then(paint);
  }

  // Late-injected UI (e.g. the nav drawer, which is cloned into the DOM only when
  // opened — after this script's first paint) can request a repaint of its
  // freshly-added [data-vmx-review-*] nodes. fetchStats() is promise-cached, so
  // repeated calls don't refetch.
  document.addEventListener('vmx:reviews:repaint', function () {
    fetchStats().then(paint);
  });

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }
})();
